<script lang="ts">
  import { cx } from '@emotion/css';
  import { TwistGroup, TwistGrid, TwistGridItem, ResponsiveVars } from '@twist/core';
  import type { AccordionItem } from '$lib/sanity/schemas';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import AccordionIcon from './AccordionIcon.svelte';
  import SanityComponentFactory from '$components/SanityComponents/SanityComponentFactory.svelte';
  import HeadingSizeDecorator from '$components/Common/HeadingSizeDecorator.svelte';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';
  let itemOpen = $state(false);

  const handleClick = () => (itemOpen = !itemOpen);

  interface Props {
    document: AccordionItem;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
    // Speed of animation in pixels per second
    speed?: number;
  }

  let { document, sourceMap, path = [], speed = 780 }: Props = $props();

  let contentHeight: number = $state(0);

  const responsiveVars = new ResponsiveVars()
    .addFluid('icon-size', toFluidVarInput(28, 35))
    .addFluid('title-padding-y', toFluidVarInput(24, 36))
    .addFluid('title-font-size', toFluidVarInput(14, 18))
    .addFluid('heading-margin-right', toFluidVarInput(36, 36))
    .addFluid('contents-padding-bottom', toFluidVarInput(60, 78))
    .toCSS();
  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));
  let gridItems = $derived(document?.contents?.gridItems ?? []);
  // time = distance / speed
  let duration = $derived(contentHeight / speed);
</script>

<TwistGroup>
  {#snippet children({ active: groupActive }: { active: boolean })}
    <button
      onclick={handleClick}
      class={cx('accordion-item', responsiveVars)}
      class:accordionOpen={itemOpen}
      class:groupActive
    >
      <div class="accordion-title">
        <svelte:element
          this={document.headingLevel}
          data-sanity={encodeDataAttribute([...path, 'headline'])}
        >
          <HeadingSizeDecorator size={document.headingLevel}>
            {document.headline}
          </HeadingSizeDecorator>
        </svelte:element>

        <div class="icon-wrapper">
          <AccordionIcon {groupActive} open={itemOpen} />
        </div>
      </div>

      <div
        class="accordion-contents"
        style="--duration: {duration}s; --height: {contentHeight}px;}"
      >
        <div class="accordion-contents-inner" bind:clientHeight={contentHeight}>
          <TwistGrid
            cols={2}
            colGaps={[
              ['min', 16],
              ['sm', 'lg', 16, 32],
              ['lg', 'max', 16, 32],
              ['max', 32]
            ]}
            rowGaps={[
              ['min', 16],
              ['sm', 'lg', 16, 32],
              ['lg', 'max', 16, 32],
              ['max', 32]
            ]}
          >
            {#each gridItems as item, index}
              <TwistGridItem
                colStart={item.placements[0].columnStart}
                colSpan={item.placements[0].columnSpan}
                rowSpan={item.placements[0].rowSpan}
                rowStart={item.placements[0].rowStart}
              >
                {#if item.component}
                  <SanityComponentFactory
                    document={item.component}
                    {sourceMap}
                    path={[...path, 'contents', 'gridItems', index, 'component']}
                  />
                {/if}
              </TwistGridItem>
            {/each}
          </TwistGrid>
        </div>
      </div>
    </button>
  {/snippet}
</TwistGroup>

<style lang="scss">
  @import 'src/scss/variables';

  .accordion-item {
    width: 100%;
    margin-top: -1px;
    border-top: $stroke-width-1 solid
      var(--theme-general-foreground-color, $border-interactive-default-color);
    border-right: none;
    border-bottom: $stroke-width-1 solid
      var(--theme-general-foreground-color, $border-interactive-default-color);
    border-left: none;
    background: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
    color: var(
      --theme-button-secondary-button-default-foreground-color,
      $text-interactive-default-color
    );
    position: relative;
    z-index: 1;

    &:hover {
      z-index: 2;
      margin-top: -1.5px;
      margin-bottom: -0.5px;
      border-top: $stroke-width-1_5 solid
        var(--theme-general-foreground-color, $border-interactive-default-hover-color);
      border-bottom: $stroke-width-1_5 solid
        var(--theme-general-foreground-color, $border-interactive-default-hover-color);
    }

    &.groupActive .accordion-title {
      color: var(
        --theme-button-secondary-button-hover-focus-foreground-color,
        $text-interactive-default-hover-color
      );
      opacity: var(--theme-button-secondary-button-hover-focus-opacity, 0.6);
    }

    &:active .accordion-title {
      color: var(
        --theme-button-secondary-button-active-foreground-color,
        $text-interactive-default-hover-color
      );
      opacity: var(--theme-button-secondary-button-active-opacity, 0.6);
    }
  }

  .accordion-title {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: var(--title-padding-y) 0;
    font-size: var(--title-font-size);
    font-family: var(--font-family-primary);
    font-weight: 700;
    line-height: 130%;
    text-decoration: none;
    cursor: pointer;
  }

  h3,
  h4 {
    text-align: left;
    align-self: center;
    margin-right: var(--heading-margin-right);
    padding: 0;
  }

  .accordion-contents {
    height: 0px;
    overflow: hidden;
    transition: all var(--duration) var(--twist-ease-out-1);
  }

  .accordionOpen .accordion-contents {
    height: var(--height);
  }

  .accordion-contents-inner {
    padding-bottom: var(--contents-padding-bottom);
  }

  .icon-wrapper {
    flex-shrink: 0;
    width: var(--icon-size);
    height: var(--icon-size);
  }
</style>
